'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * DeployVersionBanner
 *
 * Detects when the server is running a newer build than the currently-loaded
 * client bundle. Server Actions from a stale tab reference action IDs that
 * may no longer exist on the new deploy — see UnrecognizedActionError. This
 * banner warns the user to refresh *before* they trigger a stale action, so
 * they don't lose in-progress work.
 *
 * The first successful /api/version response captures the SHA of the build
 * this tab is running (the bundle was just downloaded from that build). Any
 * subsequent response with a different SHA means the server has deployed a
 * newer build → prompt refresh.
 *
 * Local dev safety: /api/version returns 'dev' when no VERCEL_GIT_COMMIT_SHA
 * is set, and the check short-circuits — no false positives running locally.
 */
const POLL_INTERVAL_MS = 5 * 60 * 1000

export function DeployVersionBanner() {
  // Baseline SHA captured on first check. Stored in a ref so updates don't
  // recreate the callback or re-run the polling effect.
  const baselineRef = useRef<string | null>(null)
  const checkingRef = useRef(false)
  const [staleVersion, setStaleVersion] = useState(false)

  const checkVersion = useCallback(async () => {
    if (checkingRef.current) return
    checkingRef.current = true
    try {
      const response = await fetch('/api/version', {
        method: 'GET',
        cache: 'no-store',
      })
      if (!response.ok) return

      const { sha } = (await response.json()) as { sha?: string }
      if (!sha || sha === 'dev') return

      if (baselineRef.current === null) {
        baselineRef.current = sha
      } else if (sha !== baselineRef.current) {
        setStaleVersion(true)
      }
    } catch {
      // Network hiccup — try again next tick.
    } finally {
      checkingRef.current = false
    }
  }, [])

  useEffect(() => {
    // Once we've flipped to stale we don't need to keep polling — refresh
    // is the only way out and it'll unmount everything anyway.
    if (staleVersion) return

    const handleFocus = () => checkVersion()
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') checkVersion()
    }

    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisibility)
    const interval = setInterval(checkVersion, POLL_INTERVAL_MS)

    // Kick off an initial check to capture the baseline.
    checkVersion()

    return () => {
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibility)
      clearInterval(interval)
    }
  }, [checkVersion, staleVersion])

  if (!staleVersion) return null

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 top-0 z-[9998] flex items-center justify-center gap-3 bg-amber-500 px-4 py-2 text-sm font-medium text-amber-950 shadow"
    >
      <span>
        A new version is available. Please refresh before continuing —
        actions on this tab may fail because the code you're running is out of date.
      </span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-md bg-amber-950 px-3 py-1 text-xs font-semibold text-amber-50 hover:bg-amber-900 focus:outline-none focus:ring-2 focus:ring-amber-950 focus:ring-offset-2 focus:ring-offset-amber-500"
      >
        Refresh
      </button>
    </div>
  )
}
